import React, { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import PhonePeUPIOutageAlert from './components/PhonePeUPIOutageAlert';

const UROPAY_SCRIPT_URL = 'https://cdn.uropay.me/uropay-embed.min.js';

const UroPayEmbedPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state || {};

  const {
    buttonId,
    apiKey,
    successRedirectUrl,
    failureRedirectUrl,
    amount,
    environment,
    pendingId,
  } = state;

  /** UroPay uses Web Crypto; browsers only expose it in a "secure context" (HTTPS or localhost). */
  const secureContextOk = useMemo(() => {
    if (typeof window === 'undefined') return true;
    return typeof window.isSecureContext !== 'undefined'
      ? window.isSecureContext
      : !!(typeof crypto !== 'undefined' && crypto.subtle);
  }, []);

  useEffect(() => {
    if (!buttonId || !apiKey) {
      navigate('/subscription-payment', { replace: true });
      return;
    }
    if (!secureContextOk) return;
    if (pendingId && typeof window !== 'undefined') {
      window.sessionStorage?.setItem('uropayPendingId', pendingId);
    }
    const script = document.createElement('script');
    script.src = UROPAY_SCRIPT_URL;
    script.async = true;
    document.body.appendChild(script);
    return () => {
      if (script.parentNode) script.parentNode.removeChild(script);
    };
  }, [apiKey, buttonId, navigate, pendingId, secureContextOk]);

  if (!buttonId || !apiKey) return null;

  if (!secureContextOk) {
    const port = typeof window !== 'undefined' && window.location?.port
      ? `:${window.location.port}`
      : ':4028';
    /** Same path loses React `location.state`; user should start checkout again from localhost. */
    const localhostSubscriptionUrl = `http://localhost${port}/subscription-payment`;

    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="mx-auto max-w-xl px-4 py-10">
          <div className="bg-card rounded-xl shadow-warm-lg p-6 md:p-8 text-left border border-warning/30">
            <h1 className="text-xl font-heading font-bold mb-2">UPI payment needs a secure context</h1>
            <p className="text-sm text-muted-foreground mb-4">
              UroPay uses the Web Crypto API, which only works on HTTPS or on{' '}
              <code className="text-xs bg-muted px-1 rounded">localhost</code>. Opening the app via a LAN IP
              (e.g. <code className="text-xs bg-muted px-1 rounded">http://192.168.x.x</code>) is not treated as
              secure, so the payment step fails with <code className="text-xs">ERR_CRYPTO_NOT_SUPPORTED</code>.
            </p>
            <p className="text-sm font-medium mb-3">Use one of these:</p>
            <ul className="text-sm text-muted-foreground list-disc pl-5 space-y-1 mb-6">
              <li>
                On this PC, open <strong>http://localhost{port}</strong>, sign in, choose a plan, and pay again — UroPay
                will work there.
              </li>
              <li>Or test on your live site over HTTPS (production).</li>
            </ul>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button type="button" variant="default" onClick={() => { window.location.href = localhostSubscriptionUrl; }}>
                Open subscription page on localhost
              </Button>
              <Button type="button" variant="outline" onClick={() => navigate('/subscription-payment')}>
                Back to plans
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="mx-auto max-w-xl px-4 py-10">
        <div className="bg-card rounded-xl shadow-warm-lg p-6 md:p-8 text-center">
          <h1 className="text-2xl font-heading font-bold mb-2">Complete UPI Payment</h1>
          <p className="text-muted-foreground mb-6">
            Use your UPI app to complete payment securely.
          </p>
          <a
            href="#"
            className="uropay-btn inline-flex items-center justify-center px-6 py-3 rounded-lg font-semibold text-white bg-primary hover:opacity-90 transition-smooth"
            data-uropay-api-key={apiKey}
            data-uropay-button-id={buttonId}
            data-uropay-environment={environment || 'LIVE'}
            data-uropay-amount={String(amount || 0)}
            data-uropay-success-redirect-url={successRedirectUrl || ''}
            data-uropay-failure-redirect-url={failureRedirectUrl || ''}
          >
            Pay INR {Number(amount || 0).toLocaleString('en-IN')}
          </a>
        </div>
        <div className="mt-6">
          <PhonePeUPIOutageAlert />
        </div>
      </main>
    </div>
  );
};

export default UroPayEmbedPage;
